/* eslint-disable @typescript-eslint/promise-function-async */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, {Children, cloneElement, isValidElement} from 'react';
import type {ReactElement, ReactNode} from 'react';

import {FilterSlot} from '../FilterSlot/FilterSlot';

type AnyPropsWithChildren = {
    children?: ReactNode;
    [key: string]: any;
} | undefined;

/**
 * Recursively processes React children to replace FilterSlot components with the provided slot content.
 * This function traverses the component tree and substitutes any FilterSlot components with the specified
 * slot ReactNode while preserving the original structure and props of other components.
 *
 * @param children The React children to process and search for FilterSlot components.
 * @param slot     The ReactNode content that will replace any found FilterSlot components.
 * @returns The processed ReactNode with FilterSlot components replaced by the slot content.
 *
 * @example
 * ```tsx
 * const slotContent = <div>Filter content</div>;
 * const processedChildren = processChildren(
 *     <div>
 *         <FilterSlot />
 *         <span>Other content</span>
 *     </div>,
 *     slotContent
 * );
 * ```
 */
export const processChildren = (
    children: ReactNode,
    slot: ReactNode
): ReactNode => Children.map(children, child => {
    if (isValidElement(child)) {
        if (child.type === FilterSlot) {
            return slot;
        }

        if ((child as ReactElement<AnyPropsWithChildren>).props?.children) {
            return cloneElement(child as ReactElement<AnyPropsWithChildren>, {
                ...(child.props ?? {}),
                children: processChildren((child as ReactElement<AnyPropsWithChildren>).props!.children, slot)
            });
        }
    }

    return child;
});